import React from "react";
import { Badge } from "@mui/material";
import ChatIcon from "@mui/icons-material/Chat";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

/**
 * @component Chat8MessagesHeader
 * @description Header colapsável do painel de mensagens do chat
 *
 * ⚠️ IMPORTANTE: Este componente SEMPRE deve iniciar com messagesExpanded=false (colapsado)
 * Isso melhora a experiência do usuário, permitindo que ele foque primeiro no painel de perguntas
 * e expanda o histórico de mensagens apenas quando necessário
 *
 * @props
 * - messageCount: Número total de mensagens
 * - assistantMessageCount: Número de mensagens do assistente
 * - userMessageCount: Número de mensagens do usuário
 * - messagesExpanded: Estado de expansão (deve iniciar como false)
 * - onToggleExpanded: Função para alternar expansão
 *
 * @behavior
 * - Inicia sempre colapsado (messagesExpanded=false)
 * - Permite expandir/recolher o histórico de mensagens
 * - Mostra estatísticas das mensagens
 * - Suporte completo a acessibilidade (ARIA, keyboard navigation)
 *
 * @author LicitarsAI Team
 * @version 1.0.0
 * @since 2024-06-20
 */

interface Chat8MessagesHeaderProps {
  messageCount: number;
  assistantMessageCount: number;
  userMessageCount: number;
  messagesExpanded: boolean;
  onToggleExpanded: () => void;
}

export const Chat8MessagesHeader: React.FC<Chat8MessagesHeaderProps> = ({
  messageCount,
  assistantMessageCount,
  userMessageCount,
  messagesExpanded,
  onToggleExpanded,
}) => {
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onToggleExpanded();
    }
  };

  return (
    <header
      id="chat8-messages-header"
      className="chat8-messages-summary"
      role="banner"
      aria-controls="chat8-messages-content"
      data-testid="chat8-messages-header"
    >
      <button
        id="chat8-messages-toggle"
        onClick={(e) => {
          e.stopPropagation();
          onToggleExpanded();
        }}
        role="button"
        tabIndex={0}
        onKeyDown={handleKeyDown}
        aria-expanded={messagesExpanded}
        aria-controls="chat8-messages-content"
        aria-label={`${messagesExpanded ? "Recolher" : "Expandir"} histórico de mensagens`}
        className="chat8-messages-toggle-button"
        data-testid="chat8-messages-toggle"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%",
          padding: "10px 12px",
          background: "linear-gradient(135deg, #1e293b 0%, #334155 100%)",
          border: "none",
          borderRadius: "8px 8px 0 0",
          cursor: "pointer",
          transition: "all 0.2s ease",
          color: "#f1f5f9",
          boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
        }}
      >
        <div
          id="chat8-messages-header-left"
          className="chat8-messages-header-left"
          data-testid="chat8-messages-header-left"
          style={{ display: "flex", alignItems: "center", gap: "12px" }}
        >
          <div
            id="chat8-messages-icon-container"
            className="chat8-messages-icon-container"
            data-testid="chat8-messages-icon-container"
            style={{ display: "flex", alignItems: "center", gap: "6px" }}
          >
            <Badge
              id="chat8-messages-badge"
              badgeContent={messageCount}
              color="primary"
              invisible={messagesExpanded || messageCount === 0}
              className="chat8-messages-badge"
              data-testid="chat8-messages-badge"
              sx={{
                "& .MuiBadge-badge": {
                  backgroundColor: "#3b82f6",
                  color: "white",
                  fontSize: "0.65rem",
                  fontWeight: 600,
                  minWidth: "18px",
                  height: "18px",
                  borderRadius: "9px",
                },
              }}
            >
              <ChatIcon
                id="chat8-messages-icon"
                className="chat8-messages-icon"
                data-testid="chat8-messages-icon"
                sx={{
                  color: messagesExpanded ? "#94a3b8" : "#3b82f6",
                  fontSize: "1.1rem",
                }}
              />
            </Badge>
            <h2
              id="chat8-messages-title"
              className="chat8-messages-title"
              data-testid="chat8-messages-title"
              style={{
                color: "#f1f5f9",
                fontWeight: 600,
                fontSize: "0.9rem",
                margin: 0,
              }}
            >
              Mensagens da Conversa
            </h2>
          </div>
        </div>

        <div
          id="chat8-messages-stats"
          className="chat8-messages-stats"
          data-testid="chat8-messages-stats"
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            fontSize: "0.75rem",
            color: "#94a3b8",
          }}
        >
          <span
            id="chat8-messages-total"
            className="chat8-messages-total"
            data-testid="chat8-messages-total"
          >
            {messageCount} total
          </span>
          <span
            id="chat8-messages-assistant"
            className="chat8-messages-assistant"
            data-testid="chat8-messages-assistant"
            style={{ color: "#10b981" }}
          >
            {assistantMessageCount} assistente
          </span>
          <span
            id="chat8-messages-user"
            className="chat8-messages-user"
            data-testid="chat8-messages-user"
            style={{ color: "#3b82f6" }}
          >
            {userMessageCount} usuário
          </span>
        </div>

        <div
          id="chat8-messages-toggle-icon"
          className="chat8-messages-toggle-icon"
          data-testid="chat8-messages-toggle-icon"
          style={{
            display: "flex",
            alignItems: "center",
            transition: "transform 0.2s ease",
          }}
        >
          <ExpandMoreIcon
            id="chat8-messages-expand"
            className={`chat8-messages-expand h-4 w-4 ${
              messagesExpanded ? "rotate-180" : ""
            }`}
            data-testid="chat8-messages-expand"
            style={{
              color: "#94a3b8",
              transition: "transform 0.2s ease",
            }}
          />
        </div>
      </button>
    </header>
  );
};
